/**
 * Uniqueness Check API Client (Story 11.14)
 *
 * React Query hook for checking episode idea uniqueness via pgvector.
 * POST /api/admin/generation/check-idea-uniqueness
 */

import { useMutation } from '@tanstack/react-query';
import { apiPost } from 'services/api';

export interface ClosestMatch {
  episodeId: string;
  episodeName: string;
  scenarioSnippet: string;
  similarity: number;
}

export interface IdeaUniquenessResult {
  ideaIndex: number;
  closestMatch: ClosestMatch | null;
}

export interface WithinBatchDuplicate {
  ideaA: number;
  ideaB: number;
  similarity: number;
}

export interface CheckUniquenessResponse {
  results: IdeaUniquenessResult[];
  withinBatchDuplicates: WithinBatchDuplicate[];
  episodesWithoutEmbeddings: number;
}

async function checkIdeaUniqueness(ideas: string[]): Promise<CheckUniquenessResponse> {
  const data = await apiPost<{
    results: {
      idea_index: number;
      closest_match: {
        episode_id: string;
        episode_name: string;
        scenario_snippet: string;
        similarity: number;
      } | null;
    }[];
    within_batch_duplicates: {
      idea_a: number;
      idea_b: number;
      similarity: number;
    }[];
    episodes_without_embeddings: number;
  }>('/api/admin/generation/check-idea-uniqueness', { ideas });

  return {
    results: data.results.map((r) => ({
      ideaIndex: r.idea_index,
      closestMatch: r.closest_match
        ? {
            episodeId: r.closest_match.episode_id,
            episodeName: r.closest_match.episode_name,
            scenarioSnippet: r.closest_match.scenario_snippet,
            similarity: r.closest_match.similarity,
          }
        : null,
    })),
    withinBatchDuplicates: data.within_batch_duplicates.map((d) => ({
      ideaA: d.idea_a,
      ideaB: d.idea_b,
      similarity: d.similarity,
    })),
    episodesWithoutEmbeddings: data.episodes_without_embeddings,
  };
}

export function useCheckIdeaUniqueness() {
  return useMutation({
    mutationFn: checkIdeaUniqueness,
  });
}
